import { Grid, Menu, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { FeedStyleClasses, TimelineStyleClasses } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterCommand, AdvFilterComponent, AdvFilterPicker, AdvFilterResult } from "components/AdvFilter";
import { CmdAccountBlockCode, CmdAccountBlocked, CmdAccountDelete, CmdAccountLockedOut, CmdAccountNotBlocked, CmdGroup, CmdIsManager, CmdKnowAbout, CmdLastAccessDateEnd, CmdLastAccessDateStart, CmdLocation, CmdLocationName, CmdRoles, CmdSpecialty, CmdWithManager, CommandLabels } from "components/AdvFilter/CommonCommands";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import IconButtonBlocked from "components/Buttons/IconButtonBlocked";
import IconButtonHierarchy from "components/Buttons/IconButtonHierarchy";
import IconButtonOrderBy from "components/Buttons/IconButtonOrderby";
import IconButtonUpload from "components/Buttons/IconButtonUpload";
import { RequestIndicator } from "components/CallServer";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CountFilter from "components/CountFilter";
import { MuiFilterAdvancedContainer, MuiFilterAppBar, MuiFilterLine, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils, { nprop } from "lib/LibUtils";
import { SystemRoles } from "models/ApiEnums";
import { GetUserListParams, PagedResult, UserAdminListDto, UserListFilter, UserListOrderBy, UserRecordFormConfigDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import UserDiagramDialog from "views/User/Dialogs/UserDiagramDialog";
import UserImportDialog from "views/User/Dialogs/UserImportDialog";
import UserEdit from "./Components/UserEdit";
import UserListCard from "./Components/UserListCard";
import ChangePasswordDialog from "./Dialogs/ChangePasswordDialog";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    searchText: string;
    advFilters: AdvFilterResult[];

    orderByMenuAnchorEl: any;
    dialog: any;
    list: PagedResult<UserAdminListDto>;
    offset: number;
    formCycle: RequestLifeCycle;
    lastError: any;
    editUserId: string;
}

interface IProps {
}

interface IRouteProps {
    id: string;
}

type Props = IProps & RouteComponentProps<IRouteProps> & WithStyles<typeof TimelineStyleClasses> & WithStyles<typeof FeedStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

export class UserAdminList extends React.Component<Props, IState> {

    private orderBy: UserListOrderBy = UserListOrderBy.Name;
    private config: UserRecordFormConfigDto;
    private isFiltered: boolean;
    private advFilterRef: AdvFilterComponent;
    private licensesRemaining = -1;
    private allLabels = CommandLabels();


    constructor(props: Props) {
        super(props);
        this.state = {
            advFilters: [],
            searchText: "",

            list: { items: null, total: 0 },
            offset: 0,
            orderByMenuAnchorEl: null,
            formCycle: RequestLifeCycle.None,
            lastError: null,
            dialog: null,

            editUserId: this.props.match.params.id
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.User.UserAdminList." + r, f);
    }

    private async getLicensesRemaining() {
        this.licensesRemaining = await api.getLicensesRemaining();
    }

    ///
    private init() {
        Promise.all([api.getUserFormEditOptions(Session.user.culture), api.getLicensesRemaining()])
            .then(([config, licensesRemaining]) => {

                this.config = config;
                this.licensesRemaining = licensesRemaining;

                if (!this.props.match.params.id)
                    this.loadData(0);
            })
            .catch((error: any) => {
                this.setState({ lastError: error });
            });
    }

    ///
    private loadData(offset: number) {
        const p: GetUserListParams = {
            filter: this.getFilter(),
            orderBy: this.orderBy,
            range: { offset: offset, take: DefaultListParameters.viewRecordsByPage }
        };

        this.setState({ formCycle: RequestLifeCycle.Loading, orderByMenuAnchorEl: null, dialog: null, editUserId: null, lastError: null });

        api.getAdminUserList(p)
            .then((result) => {
                this.setState({ list: result, formCycle: RequestLifeCycle.None, offset: offset });
            })
            .catch((error) => {
                this.setState({ list: null, lastError: error, formCycle: RequestLifeCycle.LoadFailed, offset: 0 });
            });
    }


    //#region FILTER

    ///
    private getCommands(): AdvFilterCommand[] {
        const labels = this.allLabels;
        const cmds: AdvFilterCommand[] = [
            { keyword: CmdGroup, displayName: labels[CmdGroup], picker: AdvFilterPicker.Group, group: 1 },
            { keyword: CmdKnowAbout, displayName: labels[CmdKnowAbout], picker: AdvFilterPicker.Topic, group: 1 },
            { keyword: CmdSpecialty, displayName: labels[CmdSpecialty], picker: AdvFilterPicker.Topic, group: 1 },

            { keyword: CmdIsManager, displayName: labels[CmdIsManager], group: 2 },
            { keyword: CmdWithManager, displayName: labels[CmdWithManager], picker: AdvFilterPicker.User, group: 2 },

            //{ keyword: CmdAccountNotBlocked, displayName: labels[CmdAccountNotBlocked], group: 3 },
            { keyword: CmdAccountBlocked, displayName: labels[CmdAccountBlocked], group: 3 },
            { keyword: CmdAccountBlockCode, displayName: labels[CmdAccountBlockCode], picker: AdvFilterPicker.BlockCode, group: 3 },

            { keyword: CmdAccountLockedOut, displayName: labels[CmdAccountLockedOut], group: 4 },
            { keyword: CmdAccountDelete, displayName: labels[CmdAccountDelete], group: 4 },

            { keyword: CmdRoles, displayName: labels[CmdRoles], picker: AdvFilterPicker.Roles, group: 5, canRepeat: true },

            { keyword: CmdLastAccessDateStart, displayName: labels[CmdLastAccessDateStart], picker: AdvFilterPicker.Calendar, group: 6 },
            { keyword: CmdLastAccessDateEnd, displayName: labels[CmdLastAccessDateEnd], picker: AdvFilterPicker.Calendar, group: 6 },

        ];

        if (this.config.chooseLocalFromList)
            cmds.push({ keyword: CmdLocation, displayName: this.config.localName + "...", picker: AdvFilterPicker.Local, group: 1 });
        else
            cmds.push({ keyword: CmdLocationName, displayName: this.config.localName + "...", picker: AdvFilterPicker.Text, group: 1 });

        return cmds;
    }

    ///
    private getFilter(): UserListFilter {

        const { searchText, advFilters } = this.state;

        if (searchText.length == 0 && advFilters.length == 0) {
            this.isFiltered = false;
            return {
                searchText: null,
                startDate: null,
                endDate: null,

                groups: null,
                specialties: null,
                knowsAbout: null,
                blockCodes: null,
                roles: null,

                locationName: null,
                locations: null,

                isManager: null,
                managerId: null,

                isBlocked: null,
                isNotBlocked: null,
                isDeleted: null,
                isLockedOut: null,
            };
        }
        else {

            this.isFiltered = true;

            var dts = advFilters.find(x => x.keyword == CmdLastAccessDateStart);
            var dte = advFilters.find(x => x.keyword == CmdLastAccessDateEnd);

            return {
                searchText: searchText.length == 0 ? null : searchText,
                startDate: dts ? (dts.value as Date).putStartOfDay() : null,
                endDate: dte ? (dte.value as Date).putEndOfDay() : null,

                managerId: nprop(nprop(advFilters.find(x => x.keyword == CmdWithManager)).value, null),

                isManager: advFilters.find(x => x.keyword == CmdIsManager) != null,
                isBlocked: advFilters.find(x => x.keyword == CmdAccountBlocked) != null,
                isNotBlocked: advFilters.find(x => x.keyword == CmdAccountNotBlocked) != null,

                isLockedOut: advFilters.find(x => x.keyword == CmdAccountLockedOut) != null,
                isDeleted: advFilters.find(x => x.keyword == CmdAccountDelete) != null,

                groups: advFilters.filter(x => x.keyword == CmdGroup).map(v => v.value),
                specialties: advFilters.filter(x => x.keyword == CmdSpecialty).map(v => v.value),
                knowsAbout: advFilters.filter(x => x.keyword == CmdKnowAbout).map(v => v.value),
                blockCodes: advFilters.filter(x => x.keyword == CmdAccountBlockCode).map(v => v.value),
                roles: advFilters.filter(x => x.keyword == CmdRoles).map(v => v.value),

                locations: advFilters.filter(x => x.keyword == CmdLocation).map(v => v.value),
                locationName: nprop(nprop(advFilters.find(x => x.keyword == CmdLocationName)).value, null),
            };
        }
    }

    ///
    private handleBlockedFilter() {
        const { advFilters } = this.state;
        var isBlocked = advFilters.find(x => x.keyword == CmdAccountBlocked) != null;

        var newAdvFilter: AdvFilterResult[];

        if (isBlocked)
            newAdvFilter = advFilters.filter(x => x.keyword != CmdAccountBlocked);
        else {
            newAdvFilter = advFilters.slice(0);
            newAdvFilter.push({ keyword: CmdAccountBlocked, text: this.allLabels[CmdAccountBlocked] } as AdvFilterResult);
        }

        this.setState({ advFilters: newAdvFilter }, () => this.loadData(0));
    };

    ///
    private handleTree(user: UserAdminListDto) {
        this.setState({
            dialog: (
                <UserDiagramDialog
                    rootId={user?.userId}
                    rootName={user?.name}
                    onClose={
                        () => {
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    }

    ///
    private renderAdvFilter(): JSX.Element {
        const canAdd = Session.hasRole(SystemRoles.UserManager | SystemRoles.Administrator);
        const filterBlocked = this.state.advFilters.find(x => x.keyword == CmdAccountBlocked) != null;

        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >
                    <MuiFilterLine>
                        {
                            canAdd &&
                            <div>
                                <IconButtonAdd
                                    color="inherit"
                                    title={this.res("ButtonAddTitle")}
                                    onClick={() => this.handleEdit("create")}
                                />
                                <MuiClickable
                                    onClick={() => this.handleEdit("create")}
                                >
                                    <Typography variant="button">{this.res("ButtonAddText")}</Typography>
                                </MuiClickable>
                            </div>
                        }

                        <MuiPushRight>
                            {
                                canAdd &&
                                <IconButtonUpload
                                    color="inherit"
                                    title={this.res("ButtonImportUserTitle")}
                                    onClick={() => this.handleImportUsers()}
                                />
                            }

                            {
                                this.config.corporateMode &&
                                <IconButtonHierarchy
                                    color="inherit"
                                    title={this.props.t("Views.User.Dialogs.UserDiagramDialog.BoxTitle")}
                                    onClick={() => this.handleTree(null)}
                                />
                            }

                            <IconButtonBlocked
                                color={filterBlocked ? "warning" : "inherit"}
                                title={this.allLabels[CmdAccountBlocked]}
                                onClick={() => this.handleBlockedFilter()}
                            />

                            <IconButtonOrderBy
                                color="inherit"
                                onClick={
                                    (e) => {
                                        this.setState({ orderByMenuAnchorEl: e.currentTarget });
                                    }
                                }
                            />
                            <Menu
                                id="simple-menu"
                                anchorEl={this.state.orderByMenuAnchorEl}
                                open={this.state.orderByMenuAnchorEl != null}
                                onClose={
                                    () => {
                                        this.setState({ orderByMenuAnchorEl: null });
                                    }}
                            >
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.Name")} value={UserListOrderBy.Name} currentSelection={this.orderBy} onClick={this.handleApplyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.LastModified")} value={UserListOrderBy.LastUpdateDate} currentSelection={this.orderBy} onClick={this.handleApplyOrder} />
                            </Menu>
                        </MuiPushRight>
                    </MuiFilterLine>

                    <MuiFilterAdvancedContainer disableMargins>
                        <AdvFilter
                            id="advfilter"
                            commands={this.getCommands()}
                            innerRef={(ref) => this.advFilterRef = ref}
                            placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            placeHolderFocus={this.props.t("Common.Filter.Adv.InputPlaceholderFocus")}
                            selectedCommands={this.state.advFilters}
                            title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                            onUpdateSelection={this.handleUpdateFilter} />
                    </MuiFilterAdvancedContainer>
                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    ///
    private handleApplyOrder = (orderBy: UserListOrderBy) => {
        this.orderBy = orderBy;
        this.loadData(0);
    };

    ///
    private handleUpdateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        this.setState({ searchText, advFilters }, () => this.loadData(0));
    };


    //#endregion

    ///
    private handleImportUsers() {
        this.setState({
            dialog: (
                <UserImportDialog
                    onClose={
                        async () => {
                            await this.getLicensesRemaining();
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    }

    ///
    private handleEdit(userId: string) {
        this.setState({ editUserId: userId });
    }

    ///
    private handleDelete(user: UserAdminListDto) {
        this.setState({
            dialog: (
                <MuiConfirmation
                    message={this.props.t("Common.Question.ConfirmationDeleteOfMessage", { name: user.name })}
                    onClose={(result) => {
                        if (result) {
                            this.setState({ dialog: null, formCycle: RequestLifeCycle.Posting });

                            api.deleteUser(user.userId)
                                .then(async (result) => {
                                    if (result) {
                                        this.props.snackbar.showMessage(this.res("DeleteSuccess"), "success");
                                        await this.getLicensesRemaining();
                                        this.loadData(this.state.offset);
                                    }
                                    else this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                                })
                                .catch((error) => {
                                    this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
                                });
                        }
                        else
                            this.setState({ dialog: null });
                    }}
                />
            )
        });
    }

    ///
    private handleRecover(user: UserAdminListDto) {
        this.setState({
            dialog: (
                <MuiConfirmation
                    message={this.res("ConfirmationRecoverMessage", { name: user.name })}
                    onClose={(result) => {
                        if (result) {
                            this.setState({ dialog: null, formCycle: RequestLifeCycle.Posting });

                            api.recoverUser(user.userId)
                                .then(async (result) => {
                                    if (result) {
                                        this.props.snackbar.showMessage(this.res("RecoverSuccess"), "success");
                                        await this.getLicensesRemaining();
                                        this.setState({ dialog: null, formCycle: RequestLifeCycle.None }, () => this.loadData(this.state.offset));
                                    }
                                    else {
                                        this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                                    }
                                })
                                .catch((error) => {
                                    this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
                                });
                        }
                        else
                            this.setState({ dialog: null });
                    }}
                />
            )
        });
    }

    ///
    private handleChangePassword(user: UserAdminListDto) {
        this.setState({
            dialog: (
                <ChangePasswordDialog
                    userId={user.userId}
                    requiresOldPassword={false}
                    onClose={() => { this.setState({ dialog: null }); }}
                />
            )
        });
    }

    ///
    private handleViewProfile(user: UserAdminListDto) {
        HtmlUtils.openUrlInNewWindow(RoutePaths.UserProfileViewer.replace(":id?", user.userId));
    }

    ///
    private renderItems() {
        const { list } = this.state;

        if (list.total == 0 && !this.isFiltered)
            return <MuiMessageBox variant="info" message={this.res("RsResultText_zero")} />;

        if (list.total == 0 && this.isFiltered)
            return <MuiMessageBox
                variant="warning"
                action={<ButtonResetFilter color="warning" onClick={() => this.advFilterRef?.resetFilter()} />}
                message={this.res("RsFilteredResultText_zero")}
            />;

        return (
            <div>

                <MuiContainer margin="normal" className={this.props.classes.subtitle}>
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <CountFilter
                                count={list.total}
                                filtered={this.isFiltered}
                                resource="Views.User.UserAdminList.RsResultText"
                                filteredResource="Views.User.UserAdminList.RsFilteredResultText"
                                onResetFilter={() => this.advFilterRef.resetFilter()}
                            />
                        </Grid>
                        {
                            this.licensesRemaining != -1 &&
                            <Grid item>
                                <span>{this.res("RemainingLicenses")}: {this.licensesRemaining}</span>
                            </Grid>
                        }
                    </Grid>
                </MuiContainer>

                {
                    list.items.map((item, index) => (
                        <MuiContainer margin="dense" key={index} >
                            <UserListCard
                                classes={this.props.classes}
                                user={item}
                                key={index}
                                onChangePassword={() => this.handleChangePassword(item)}
                                onDelete={() => this.handleDelete(item)}
                                onEdit={() => this.handleEdit(item.userId)}
                                onRecover={() => this.handleRecover(item)}
                                onViewProfile={() => this.handleViewProfile(item)}
                            />
                        </MuiContainer>
                    ))
                }
                <MuiPagination
                    limit={DefaultListParameters.viewRecordsByPage}
                    offset={this.state.offset}
                    total={this.state.list.total}
                    onClick={(e, offset) => this.loadData(offset)}
                />
            </div>
        );
    }

    ///
    private renderList() {
        const { lastError, formCycle } = this.state;

        if (formCycle == RequestLifeCycle.LoadFailed)
            return <RequestErrorReport error={lastError} />;

        return (
            <>
                {this.state.dialog}

                <RequestIndicator
                    open={formCycle == RequestLifeCycle.Posting}
                    type="post"
                />

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                {this.renderAdvFilter()}

                {
                    formCycle == RequestLifeCycle.Loading
                        ? <LoadingIndicator />
                        : this.renderItems()
                }
            </>
        );
    }

    ///
    public componentDidMount() {
        this.init();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (!this.props.match.params.id && prevProps.match.params.id)
            this.loadData(0);
    }

    //
    public render() {

        if (this.config == null) {
            return <RequestIndicator type="waiting" />;
        }

        const { editUserId } = this.state;

        if (editUserId != null)
            return (
                <UserEdit
                    userId={editUserId}
                    gobackMessage={this.res("ButtonGoBackText")}
                    onClose={
                        async (reload) => {
                            await this.getLicensesRemaining();

                            if (this.props.match.params.id)
                                this.props.history.replace(LibUtils.removeRouterOptionalParameters(RoutePaths.UserAdminList));
                            else if (reload === true || this.state.list.items == null)
                                this.loadData(this.state.offset);
                            else
                                this.setState({ editUserId: null });
                        }
                    }
                />
            );

        return (
            <div className={this.props.classes.timelineContainer}>
                {this.renderList()}
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);

export default withRouter(withSnackbar(withTranslation()(withAllStyles(UserAdminList))));
